/**
 * Roles, mirrored from functions/src/utils/roles.ts.
 *
 * This copy exists so the UI can decide what to render. It is NOT access
 * control: every callable re-checks the caller's roles server-side from custom
 * claims, and Firestore rules deny by default independently of both. Anything
 * this file hides is still reachable by anyone with devtools, and must be
 * refused by the backend when they reach it.
 *
 * Keep the union in step with the backend. If they drift, the UI shows doors
 * that do not open, which is a worse failure than showing none.
 */

export const ALL_ROLES = [
  'viewer',
  'author',
  'reviewer',
  'approver',
  'admin',
  'sysadmin',
] as const;

export type Role = (typeof ALL_ROLES)[number];

/**
 * The roles an organization administrator may hand out.
 *
 * Sysadmin is excluded deliberately, mirroring ASSIGNABLE_ROLES on the
 * backend: cross-organization access is not something a customer grants
 * inside their own tenant, and the backend refuses it regardless.
 */
export const ASSIGNABLE_ROLES = [
  'viewer',
  'author',
  'reviewer',
  'approver',
  'admin',
] as const satisfies readonly Role[];

export const ROLE_DISPLAY_NAMES: Record<Role, string> = {
  viewer: 'Viewer',
  author: 'Author',
  reviewer: 'Reviewer',
  approver: 'Approver',
  admin: 'Administrator',
  sysadmin: 'System Admin',
};

/** Higher is more privileged. Mirrors ROLE_LEVELS on the backend. */
export const ROLE_LEVELS: Record<Role, number> = {
  viewer: 1,
  author: 2,
  reviewer: 3,
  approver: 4,
  admin: 5,
  sysadmin: 6,
};

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ALL_ROLES as readonly string[]).includes(value);
}

/**
 * Whether any held role reaches `required`.
 *
 * A member holds an ARRAY of roles — author and approver at once is normal in
 * a hospital small enough that the person who writes a policy also signs it
 * off — so this asks about the highest one held, never about a single value.
 */
export function hasAtLeast(held: readonly Role[], required: Role): boolean {
  const need = ROLE_LEVELS[required];
  return held.some((r) => ROLE_LEVELS[r] >= need);
}

export function has(held: readonly Role[], role: Role): boolean {
  return held.includes(role);
}
